import { Injectable } from '@angular/core';
import { environment } from './../environments/environment';

/**
 * Provisional id for whoever opened a signup.
 *
 * Minted the first time it is asked for — which is when the signup loads — and
 * held for the life of the browser tab. It stands in as a dummy user id before
 * the person has registered and become a real customer, so the calls they make
 * on the way through (analytics, transactions, validation, registration,
 * subscription) can be stitched back into one journey.
 *
 * Format: `us-<epoch-ms>-<random>`. The timestamp makes it sortable and tells
 * you when the signup was opened; the random suffix stops two people who open
 * in the same millisecond from sharing an id.
 *
 * It is NOT a security token. It identifies a browser tab, nothing more, and
 * anyone can read or forge it. Never treat it as proof of who someone is.
 */

/** sessionStorage key. Also the payload field name. */
const USESSION_KEY = 'uSession';

@Injectable({ providedIn: 'root' })
export class USessionService {

  /** Yes/no switch. Opt-in: anything other than true means nothing is sent. */
  public get enabled(): boolean {
    return (environment.app as any)?.sendUSession === true;
  }

  /**
   * The key for this tab, creating it on first use. Survives reloads and
   * back/forward within the tab; a new tab is a new signup and a new key.
   */
  public get(): string {
    try {
      const existing = sessionStorage.getItem(USESSION_KEY);
      if (existing) return existing;

      const created = `us-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(USESSION_KEY, created);
      return created;
    } catch {
      // Private mode with storage blocked. Returning a fresh id keeps the field
      // populated; it just will not correlate across calls.
      return `us-${Date.now()}`;
    }
  }

  /**
   * Adds `uSession` to a payload when the flag is on, leaving it alone when off
   * or when the payload is not a plain object (FormData, a string body, null).
   * Never overwrites a uSession already set by the caller.
   */
  public attach<T>(payload: T): T {
    if (!this.enabled) return payload;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
    if ((payload as any)[USESSION_KEY]) return payload;

    return { ...(payload as any), [USESSION_KEY]: this.get() };
  }

  /** Field name, so callers do not hardcode the string. */
  public get fieldName(): string {
    return USESSION_KEY;
  }

  /**
   * Spreadable fragment for payloads built as object literals — the analytics
   * call, which goes out through `fetch` and so never reaches the interceptor.
   * Returns {} when the flag is off, adding nothing.
   */
  public attachAnalytics(): Record<string, string> {
    return this.enabled ? { [USESSION_KEY]: this.get() } : {};
  }

  /**
   * Appends the key to a FormData body — the subscription call, which is
   * multipart over `fetch`. Neither the interceptor (fetch) nor attach()
   * (which deliberately skips FormData) can reach that one.
   */
  public attachForm(formData: FormData): FormData {
    if (this.enabled && formData && !formData.has(USESSION_KEY)) {
      formData.append(USESSION_KEY, this.get());
    }
    return formData;
  }
}
